import React, { useCallback } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { styled, css } from '@compiled/react';

import ChevronDownIcon from '@atlaskit/icon/core/chevron-down';
import AlignTextLeftIcon from '@atlaskit/icon/core/align-text-left';
import AlignTextCenterIcon from '@atlaskit/icon/core/align-text-center';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Flex, Inline, xcss } from '@atlaskit/primitives';
import DropdownMenu, { DropdownItemGroup, DropdownItem } from '@atlaskit/dropdown-menu';
import Button from '@atlaskit/button/standard-button';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';

import { TitleAlignmentType } from '@confluence/content-topper/entry-points/contentTopperConstants.ts';
import { useTitleContentPropertiesForEditor } from '@confluence/content-topper/entry-points/useTitleContentPropertiesForEditor.ts';

const dropdownContainerStyles = xcss({
	paddingRight: 'space.075',
	paddingLeft: 'space.075',
});

const dropdownMenuStyles = css({
	width: '50px',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const IconStyles = styled.div<{
	isSelected: boolean;
}>({
	height: '32px',
	width: '32px',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles
	border: ({ isSelected }) =>
		isSelected ? `${token('border.width')} solid ${token('color.border.selected')}` : 'none',
	// eslint-disable-next-line @atlaskit/design-system/no-unsafe-design-token-usage
	borderRadius: token('radius.small', '3px'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'& button': {
		borderRadius: token('radius.xsmall'),
		paddingInline: token('space.100'),
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors, @atlaskit/ui-styling-standard/no-nested-selectors
	'& button::before': {
		background: 'none',
	},
});

const i18n = defineMessages({
	leftAlignmentIconLabel: {
		id: 'title-area-content-components.toolbar-above-title.left-alignment-icon.label',
		defaultMessage: 'Align Left',
		description: 'Label for the left alignment icon in floating toolbar above title',
	},
	centerAlignmentIconLabel: {
		id: 'title-area-content-components.toolbar-above-title.center-alignment-icon.label',
		defaultMessage: 'Align Center',
		description: 'Label for the center alignment icon in floating toolbar above title',
	},
	dropdownMenuLeftAlignedIconLabel: {
		id: 'title-area-content-components.toolbar-above-title.dropdown-menu-left-aligned-icon.label',
		defaultMessage: 'Left Aligned',
		description:
			'Label for the left alignment icon in alignment dropdown menu of the floating toolbar above title',
	},
	dropdownMenuCenterAlignedIconLabel: {
		id: 'title-area-content-components.toolbar-above-title.dropdown-menu-center-aligned-icon.label',
		defaultMessage: 'Center Aligned',
		description:
			'Label for the center alignment icon in alignment dropdown menu of the floating toolbar above title',
	},
	dropdownMenuAlignmentMenuLabel: {
		id: 'title-area-content-components.toolbar-above-title.dropdown-menu-alignment-icon.label',
		defaultMessage: 'Select for alignment options',
		description: 'Label for the alignment dropdown menu in floating toolbar above title',
	},
	headerAlignmentTooltip: {
		id: 'title-area-content-components.toolbar-above-title.header-alignment-tooltip',
		defaultMessage: 'Header alignment',
		description: 'Tooltip text for header alignment trigger button',
	},
	leftAlignHeadingTooltip: {
		id: 'title-area-content-components.toolbar-above-title.left-align-heading-tooltip',
		defaultMessage: 'Left align header',
		description: 'Tooltip text for left align heading button',
	},
	centerAlignHeadingTooltip: {
		id: 'title-area-content-components.toolbar-above-title.center-align-heading-tooltip',
		defaultMessage: 'Center align header',
		description: 'Tooltip text for center align heading button',
	},
});

export const AlignmentComponent = ({
	alignmentButtonContainer,
	userOffline,
	isDraftLockedForEditing,
	createAnalyticsEvent,
	flags,
	contentType,
	alignmentButtonState,
	coverPictureState,
	contentId,
	setIsAlignmentDropdownOpen,
	isAlignmentDropdownOpen,
}) => {
	const intl = useIntl();
	const updateAlignmentState = alignmentButtonState?.toggleAlignmentButton;
	const updateCoverPictureWidthState = coverPictureState?.toggleCoverPictureWidthButton;

	const { updatePageTitleProperty, isUpdatingAlignment } = useTitleContentPropertiesForEditor({
		contentId,
		updateAlignmentState,
		updateCoverPictureWidthState,
		contentType,
		flags,
	});

	const createAlignmentButtonAnalytics = useCallback(
		(alignment: string) => {
			const alignmentName = alignment.charAt(0).toUpperCase() + alignment.slice(1);
			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					action: 'clicked',
					source: `${alignment}AlignmentButton`,
					actionSubject: 'button',
					actionSubjectId: `pageTitle${alignmentName}AlignButton`,
				},
			}).fire();
		},
		[createAnalyticsEvent],
	);
	const onAlignmentButtonClick = async (alignment: TitleAlignmentType) => {
		if (alignmentButtonContainer.state.alignmentButtonState === alignment || isUpdatingAlignment) {
			// don't mutate state since it's the current state or it's already trying to mutate the state
			return;
		}
		void alignmentButtonContainer.setIsUpdatingAlignment(true);
		void alignmentButtonContainer.toggleAlignmentButton(alignment);
		await updatePageTitleProperty(JSON.stringify({ titleLayoutAlignment: alignment }));
		void alignmentButtonContainer.setIsUpdatingAlignment(false);
	};
	return (
		<div css={dropdownMenuStyles}>
			<DropdownMenu<HTMLButtonElement>
				trigger={({ triggerRef, ...props }) => (
					<Tooltip content={intl.formatMessage(i18n.headerAlignmentTooltip)} position="bottom">
						<Button
							{...props}
							iconBefore={
								<Inline space="space.075">
									{alignmentButtonContainer.state.alignmentButtonState ===
									TitleAlignmentType.LEFT ? (
										<AlignTextLeftIcon
											label={intl.formatMessage(i18n.dropdownMenuLeftAlignedIconLabel)}
										/>
									) : (
										<AlignTextCenterIcon
											label={intl.formatMessage(i18n.dropdownMenuCenterAlignedIconLabel)}
										/>
									)}
									<ChevronDownIcon label="" size="small" />
								</Inline>
							}
							ref={triggerRef}
							label={`${intl.formatMessage(i18n.dropdownMenuAlignmentMenuLabel)}: ${
								alignmentButtonContainer.state.alignmentButtonState === TitleAlignmentType.LEFT
									? intl.formatMessage(i18n.dropdownMenuLeftAlignedIconLabel)
									: intl.formatMessage(i18n.dropdownMenuCenterAlignedIconLabel)
							}`}
							testId={`${
								alignmentButtonContainer.state.alignmentButtonState === TitleAlignmentType.LEFT
									? 'left'
									: 'center'
							}-alignment-dropdown-menu`}
							appearance="subtle"
							shouldFitContainer
							isDisabled={userOffline || !!isDraftLockedForEditing}
						/>
					</Tooltip>
				)}
				spacing="compact"
				onOpenChange={() => setIsAlignmentDropdownOpen(!isAlignmentDropdownOpen)}
			>
				<DropdownItemGroup>
					<Flex direction="row" columnGap="space.025" xcss={dropdownContainerStyles}>
						<IconStyles
							isSelected={
								alignmentButtonContainer.state.alignmentButtonState === TitleAlignmentType.LEFT
							}
						>
							<Tooltip content={intl.formatMessage(i18n.leftAlignHeadingTooltip)}>
								<DropdownItem
									onClick={() => {
										createAlignmentButtonAnalytics(TitleAlignmentType.LEFT.toLowerCase());
										void onAlignmentButtonClick(TitleAlignmentType.LEFT);
									}}
									isDisabled={userOffline || alignmentButtonContainer.state.isUpdatingAlignment}
									isSelected={
										alignmentButtonContainer.state.alignmentButtonState === TitleAlignmentType.LEFT
									}
								>
									<AlignTextLeftIcon
										label={intl.formatMessage(i18n.leftAlignmentIconLabel)}
										testId="left-alignment-button"
									/>
								</DropdownItem>
							</Tooltip>
						</IconStyles>
						<IconStyles
							isSelected={
								alignmentButtonContainer.state.alignmentButtonState === TitleAlignmentType.CENTER
							}
						>
							<Tooltip content={intl.formatMessage(i18n.centerAlignHeadingTooltip)}>
								<DropdownItem
									onClick={() => {
										createAlignmentButtonAnalytics(TitleAlignmentType.CENTER.toLowerCase());
										void onAlignmentButtonClick(TitleAlignmentType.CENTER);
									}}
									isDisabled={userOffline || alignmentButtonContainer.state.isUpdatingAlignment}
									isSelected={
										alignmentButtonContainer.state.alignmentButtonState ===
										TitleAlignmentType.CENTER
									}
								>
									<AlignTextCenterIcon
										label={intl.formatMessage(i18n.centerAlignmentIconLabel)}
										testId="center-alignment-button"
									/>
								</DropdownItem>
							</Tooltip>
						</IconStyles>
					</Flex>
				</DropdownItemGroup>
			</DropdownMenu>
		</div>
	);
};
